import { debounce } from 'lodash';
import { useRouter } from 'next/router';
import { useEffect, useRef } from 'react';

type Route = { path?: string; userOrOrgId?: string } | null;

type UseLoginRouteHandlerReturn = {
  active?: Route;
  previous?: Route;
  handleLoginRedirection: () => void;
};

const IGNORE_PATHS = ['/playground', '/compute'];
const AUTH_ROUTES = ['/login', '/signup', '/verify-email'];
const DEFAULT_ROUTE_TO_REDIRECT = '/home';

function nullifyRouteForAuthRoutes(route?: Route): Route {
  if (!route) return null;

  if (isAnAuthRoute(route)) {
    return null;
  }

  return route;
}

function isAnAuthRoute(route?: Route): boolean {
  return AUTH_ROUTES.includes(pathWithoutQueryParams(route?.path || ''));
}

function pathWithoutQueryParams(path?: string) {
  return path?.split('?')[0] || '';
}

export const useLoginRouteHandler = (): UseLoginRouteHandlerReturn => {
  const {
    asPath,
    query: { userOrOrgId },
    replace,
  } = useRouter();

  const debouncedReplace = debounce(() => {
    // Need to ensure that we are not redirecting back to auth routes once this login handler is called.
    // As this is intended to redirect a logged-in user to non-auth pages.
    const previousRoute = nullifyRouteForAuthRoutes(ref.current?.previous);
    const currentRoute = nullifyRouteForAuthRoutes(ref.current?.active);

    // If the route is in IGNORE PATHS, do not redirect, to avoid problematic behavior:
    if (IGNORE_PATHS.some((path) => currentRoute?.path?.startsWith(path))) {
      return;
    }

    // Redirect to default route if user is in auth pages
    const isUserAtAuthRoute = isAnAuthRoute(currentRoute);
    const redirectToPath = isUserAtAuthRoute ? DEFAULT_ROUTE_TO_REDIRECT : previousRoute?.path ?? DEFAULT_ROUTE_TO_REDIRECT;

    /**
     * Redirect when:
     * 1. The current path is a null route (i.e., an auth route).
     * 2. The previous route is a null route and the current route is /explore.
     */
    if (!currentRoute?.path || (!previousRoute?.path && pathWithoutQueryParams(currentRoute.path) === '/explore')) {
      replace(redirectToPath);
    }
  }, 300);

  const ref = useRef<{ active: { path: string; userOrOrgId: string }; previous: { path: string; userOrOrgId: string } | null } | null>(null);

  useEffect(() => {
    ref.current = {
      active: { path: asPath, userOrOrgId: userOrOrgId as string },
      previous: ref?.current?.active || null,
    };
    return () => {
      debouncedReplace.cancel();
    };
  }, [asPath]);

  const handleLoginRedirection = () => {
    debouncedReplace?.cancel();
    debouncedReplace();
  };

  return { ...ref.current, handleLoginRedirection };
};
